/**
 * SeverSkillEffect — Blades tree rend path evolution: replaces Rend.
 *
 * Dragging lateral cut that applies 2 bleed stacks AND suppresses all
 * healing/regeneration for 8 seconds. The wound and the suppression
 * arrive in a single motion. Medium cooldown.
 *
 * Inherits upgrade effects from Rend via the resolver (queries with
 * skillId 'rend' because Lingering Wound/Deep Cut enhance 'rend'):
 * - Lingering Wound: `bleedDurationBonusMs` (additive) — extends bleed
 * - Deep Cut: `bleedStacksApplied` (max) — minimum 2, Deep Cut redundant
 *
 * Implements SkillEffect directly with own cooldown management.
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see blades-spec.md § Sever
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SeverDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly combat: {
    startAttack(attackType: string): number;
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      sourceSkillId: string;
      durationMs: number;
      intensity?: number;
      stacks?: number;
    }): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SeverConfig {
  readonly cooldownMs: number;
  readonly baseDurationMs: number;
  readonly bleedIntensity: number;
  readonly defaultStacksPerHit: number;
  readonly healSuppressionDurationMs: number;
}

const DEFAULT_CONFIG: SeverConfig = {
  cooldownMs: 1000,                      // medium cooldown tier (same as Rend)
  baseDurationMs: 6000,                  // 6s base bleed duration
  bleedIntensity: 3,                     // 3 damage per tick per stack
  defaultStacksPerHit: 2,               // Sever always applies at least 2
  healSuppressionDurationMs: 8000,       // 8s healing suppression
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SeverSkillEffect implements SkillEffect {
  readonly skillId = 'rend_evolved';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: SeverDeps;
  private readonly config: SeverConfig;

  private _cooldownRemaining = 0;
  private _attacksFired = 0;
  private _bleedApplications = 0;
  private _healSuppressionApplications = 0;
  private _resolvedDurationMs = 0;
  private _resolvedStacksPerHit = 2;

  constructor(deps: SeverDeps, config: Partial<SeverConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get attacksFired(): number { return this._attacksFired; }
  get bleedApplications(): number { return this._bleedApplications; }
  get healSuppressionApplications(): number { return this._healSuppressionApplications; }
  get resolvedDurationMs(): number { return this._resolvedDurationMs; }
  get resolvedStacksPerHit(): number { return this._resolvedStacksPerHit; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve bleed duration (base + Lingering Wound), scoped to 'rend' for inheritance
    const durationBonus = this.deps.resolver.getAdditiveEffect('rend', 'bleedDurationBonusMs');
    this._resolvedDurationMs = this.config.baseDurationMs + durationBonus;

    // Resolve stacks per hit (default 2, Deep Cut also 2 — max ensures minimum)
    this._resolvedStacksPerHit = Math.max(
      this.config.defaultStacksPerHit,
      this.deps.resolver.getMaxEffect('rend', 'bleedStacksApplied') ?? this.config.defaultStacksPerHit,
    );

    // Fire the lateral cut
    const enemiesHit = this.deps.combat.startAttack('sever');
    this._attacksFired++;

    if (enemiesHit > 0) {
      // Apply bleed stacks
      this.deps.statusEffects.apply('bleed', {
        sourceSkillId: 'sever',
        durationMs: this._resolvedDurationMs,
        intensity: this.config.bleedIntensity,
        stacks: this._resolvedStacksPerHit,
      });
      this._bleedApplications++;

      // Apply heal suppression
      // STUB: deferred — no enemy healing system reads this effect
      this.deps.statusEffects.apply('heal_suppression', {
        sourceSkillId: 'sever',
        durationMs: this.config.healSuppressionDurationMs,
      });
      this._healSuppressionApplications++;
    }

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
